import React, { useEffect, useState } from "react";
import { api, auth } from "../lib/api";
import { Card, PageHeader, Pill, Btn } from "../components/UI";
import { Check, Loader2, HeartHandshake, CreditCard } from "lucide-react";

const usd = (n) => "$" + new Intl.NumberFormat().format(Math.round(n || 0));

const NOTICE_STYLES = {
  ok: "border-emerald-400/40 bg-emerald-400/10 text-emerald-200",
  warn: "border-amber-400/40 bg-amber-400/10 text-amber-200",
  info: "border-cyan-400/40 bg-cyan-400/10 text-cyan-200",
};

export default function Billing() {
  const user = auth.getUser();
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  const [campaigns, setCampaigns] = useState([]);
  const [clients, setClients] = useState([]);
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [fees, setFees] = useState(null);
  const [busyPlan, setBusyPlan] = useState(null);
  const [notice, setNotice] = useState(null);

  const loadSub = async () => {
    const [p, c] = await Promise.all([api.get("/subscriptions/plans"), api.get("/subscriptions/current")]);
    setPlans((p.data || []).filter(x => x.active !== false));
    setCurrent(c.data.subscription || null);
  };

  useEffect(() => { (async () => {
    const [c, cl] = await Promise.all([api.get("/campaigns"), api.get("/clients")]);
    setCampaigns(c.data); setClients(cl.data);
    api.get("/pricing/fees").then(r => setFees(r.data)).catch(() => {});
    loadSub().catch(() => {});
  })(); }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("sub_session");
    if (params.get("sub_canceled")) {
      setNotice({ kind: "warn", text: "Checkout canceled — no changes were made." });
      window.history.replaceState({}, "", "/billing");
      return;
    }
    if (!sid) return;
    window.history.replaceState({}, "", "/billing");
    let tries = 0;
    setNotice({ kind: "info", text: "Confirming your subscription…" });
    const tick = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/subscriptions/status/${sid}`);
        if (data.payment_status === "paid") {
          setNotice({ kind: "ok", text: "Subscription active — welcome aboard!" });
          loadSub();
          return;
        }
        if (data.status === "expired") { setNotice({ kind: "warn", text: "Checkout session expired — try again." }); return; }
      } catch { /* keep polling */ }
      if (tries < 8) setTimeout(tick, 2000);
      else setNotice({ kind: "warn", text: "We couldn't confirm the payment yet — refresh in a minute or contact support." });
    };
    tick();
  }, []);

  const subscribe = async (planKey) => {
    setBusyPlan(planKey);
    try {
      const { data } = await api.post("/subscriptions/checkout", { plan_key: planKey, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      setNotice({ kind: "warn", text: e?.response?.data?.detail || "Could not start checkout." });
      setBusyPlan(null);
    }
  };

  const cancelSub = async () => {
    if (!window.confirm("Cancel your plan at the end of the current billing period?")) return;
    try {
      await api.post("/subscriptions/cancel");
      setNotice({ kind: "ok", text: "Your plan will cancel at the end of the billing period." });
      loadSub();
    } catch (e) {
      setNotice({ kind: "warn", text: e?.response?.data?.detail || "Could not cancel." });
    }
  };

  const clientMap = Object.fromEntries(clients.map(c => [c.id, c]));
  const rows = campaigns.map(c => {
    const budget = c.total_budget || 0;
    const mediaCost = budget * (1 - (c.margin || 0));
    const margin = budget * (c.margin || 0);
    return { ...c, mediaCost, marginAmt: margin, marginPct: (c.margin || 0) * 100, client: clientMap[c.client_id]?.business_name };
  });
  const totals = rows.reduce((a, r) => ({
    budget: a.budget + r.total_budget, cost: a.cost + r.mediaCost, margin: a.margin + r.marginAmt,
  }), { budget: 0, cost: 0, margin: 0 });
  const blendedMargin = totals.budget ? (totals.margin / totals.budget) * 100 : 0;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Account"
        title="Plans & Billing"
        subtitle="Your TotalReach subscription, media billing, and fundraising fees — all in one place."
      />

      {notice && (
        <div className={`rounded-md border px-4 py-3 text-sm flex items-center gap-2 ${NOTICE_STYLES[notice.kind]}`} data-testid="billing-notice">
          {notice.kind === "info" && <Loader2 className="w-4 h-4 animate-spin" />}
          {notice.text}
        </div>
      )}

      {/* Current plan */}
      <Card data-testid="current-plan-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400/20 to-blue-500/10 border border-cyan-400/30 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-cyan-300" />
            </div>
            <div>
              {current ? (
                <>
                  <div className="font-display text-lg font-bold">
                    {current.plan_name} <span className="text-slate-400 font-normal text-sm">— {usd(current.price_monthly)}/mo</span>
                  </div>
                  <div className="text-xs text-slate-400">
                    Status: <span className={current.status === "active" ? "text-emerald-300" : "text-amber-300"}>{current.status}</span>
                    {current.cancel_at_period_end && <span className="text-amber-300"> · cancels at period end</span>}
                  </div>
                </>
              ) : (
                <>
                  <div className="font-display text-lg font-bold">No active plan</div>
                  <div className="text-xs text-slate-400">Choose a monthly plan below to unlock the full platform.</div>
                </>
              )}
            </div>
          </div>
          {current && current.status === "active" && !current.cancel_at_period_end && (
            <Btn variant="secondary" onClick={cancelSub} data-testid="cancel-subscription-btn">Cancel plan</Btn>
          )}
        </div>
      </Card>

      {/* Plans */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="plans-grid">
        {plans.map(p => {
          const isCurrent = current?.plan_key === p.key && current?.status === "active";
          return (
            <div key={p.key} data-testid={`plan-card-${p.key}`}
              className={`rounded-xl border p-5 flex flex-col ${p.highlight ? "border-cyan-400/50 bg-gradient-to-b from-cyan-400/10 to-transparent" : "border-white/10 bg-white/[0.02]"}`}>
              {p.highlight && <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold mb-1.5">Most popular</div>}
              <div className="font-display text-xl font-bold">{p.name}</div>
              <div className="mt-2">
                <span className="font-display text-3xl font-bold brand-gradient">{p.price_label || usd(p.price_monthly)}</span>
                <span className="text-slate-400 text-sm">/mo</span>
              </div>
              <div className="text-xs text-slate-400 mt-1.5">{p.tagline}</div>
              <ul className="mt-4 space-y-2 flex-1">
                {(p.features || []).map((f, i) => (
                  <li key={i} className="text-xs text-slate-300 flex gap-2">
                    <Check className="w-3.5 h-3.5 text-cyan-300 shrink-0 mt-px" /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => !isCurrent && subscribe(p.key)}
                disabled={isCurrent || busyPlan === p.key}
                data-testid={`subscribe-${p.key}`}
                className={`mt-5 w-full rounded-full px-4 py-2.5 text-sm font-semibold transition-opacity inline-flex items-center justify-center gap-2 ${
                  isCurrent
                    ? "bg-white/5 border border-white/10 text-slate-400 cursor-default"
                    : "bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 hover:opacity-90 disabled:opacity-50"
                }`}>
                {busyPlan === p.key && <Loader2 className="w-4 h-4 animate-spin" />}
                {isCurrent ? "Current plan" : current ? "Switch plan" : "Subscribe"}
              </button>
            </div>
          );
        })}
      </div>

      {/* Fundraising pricing note */}
      {fees && (
        <Card data-testid="fundraising-pricing-note">
          <div className="flex items-start gap-3 text-sm text-slate-300">
            <HeartHandshake className="w-4 h-4 text-cyan-300 mt-0.5 shrink-0" />
            <div>
              <span className="text-white font-medium">Fundraising is priced separately.</span> Each contribution incurs a {fees.platform_fee_pct}% platform fee plus payment processing ({fees.merchant_fee_pct}% + ${Number(fees.stripe_fixed_fee).toFixed(2)}), deducted automatically before the net amount reaches your committee. Media spend is billed at TotalReach rates. AI tokens are billed at a +{fees.ai_premium_pct}% premium over provider cost.
            </div>
          </div>
        </Card>
      )}

      {/* Internal margin (admins only — supplier costs are never shown to clients) */}
      {isAdmin && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <div className="text-xs uppercase tracking-wider text-slate-400">Total billings</div>
              <div className="font-display text-3xl font-bold mt-2">{usd(totals.budget)}</div>
            </Card>
            <Card>
              <div className="text-xs uppercase tracking-wider text-slate-400">Media cost</div>
              <div className="font-display text-3xl font-bold mt-2">{usd(totals.cost)}</div>
            </Card>
            <Card>
              <div className="text-xs uppercase tracking-wider text-slate-400">Gross margin</div>
              <div className="font-display text-3xl font-bold mt-2 brand-gradient">{usd(totals.margin)}</div>
            </Card>
            <Card>
              <div className="text-xs uppercase tracking-wider text-slate-400">Blended margin %</div>
              <div className="font-display text-3xl font-bold mt-2 text-emerald-300">{blendedMargin.toFixed(1)}%</div>
            </Card>
          </div>

          <Card padded={false}>
            <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-display text-lg font-bold">Campaign margin breakdown</h3>
              <span className="text-[10px] uppercase tracking-wider text-slate-500">Internal — admins only</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="billing-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
                    <th className="px-5 py-3">Campaign</th>
                    <th className="px-5 py-3">Client</th>
                    <th className="px-5 py-3">Budget</th>
                    <th className="px-5 py-3">Media cost</th>
                    <th className="px-5 py-3">Margin</th>
                    <th className="px-5 py-3">Margin %</th>
                    <th className="px-5 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id} className="border-b border-white/5">
                      <td className="px-5 py-2 text-white">{r.name}</td>
                      <td className="px-5 py-2 text-slate-300">{r.client}</td>
                      <td className="px-5 py-2 font-mono">{usd(r.total_budget)}</td>
                      <td className="px-5 py-2 font-mono text-slate-300">{usd(r.mediaCost)}</td>
                      <td className="px-5 py-2 font-mono text-cyan-300">{usd(r.marginAmt)}</td>
                      <td className="px-5 py-2 font-mono text-emerald-300">{r.marginPct.toFixed(0)}%</td>
                      <td className="px-5 py-2"><Pill status={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
